/**
 * Screen-share-only sessions: someone in the meeting as a screen, from the
 * dashboard's Screen Share button, rather than as a person.
 *
 * A share session has an identity of its own, `share_<accountId>`, so the same
 * account can also be in the meeting on another device without the two
 * colliding on one Presence row or one SFU peer (contract §1 in
 * docs/screen-share-only-join-checkpoint.md). This file is the only place in
 * the web client that knows the prefix.
 */

/** Every share session's id starts with this, and no other id does. */
export const SHARE_ID_PREFIX = 'share_'

/**
 * Whether this peer is a share session.
 *
 * The Presence flag first: the server sets `share_only` when it tracks the
 * session, so a viewer knows before the first render. The prefix covers what
 * carries an id but no meta (SFU pushes, roles, moderation targets) and a
 * server that does not send the flag yet.
 */
export function isShareOnlyPeer(
  peerId: string | null | undefined,
  meta?: { share_only?: unknown } | null,
): boolean {
  if (meta?.share_only === true) return true
  return typeof peerId === 'string' && peerId.startsWith(SHARE_ID_PREFIX)
}

/**
 * The account behind a share session's id — the person's own peer id in the
 * same meeting — or null for any id that is not a share session's.
 *
 * The Screen Share page asks the room's roles about this id: a page whose
 * account is a live host or co-host is not held to the room's "screen
 * sharing off" switch, as moderators are not (contract §7c).
 */
export function accountIdOfShare(peerId: string | null | undefined): string | null {
  if (typeof peerId !== 'string' || !peerId.startsWith(SHARE_ID_PREFIX)) return null
  const accountId = peerId.slice(SHARE_ID_PREFIX.length)
  return accountId || null
}
